import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SvgIcon } from '../../components/icons';
import { DsRoot, useDsTheme } from '../ThemeProvider';
import { THEMES, getTheme, isThemeId } from '../themes';
import { AppFrame } from '../shell/AppFrame';
import { ComponentGallery } from './ComponentGallery';
import { WarrantyDemo } from './WarrantyDemo';

/**
 * Strip across the top of a demo, for hopping between directions without
 * going back to the index. Navigating is what changes the theme, which keeps
 * the URL and the active theme in sync.
 */
const ThemeSwitcherBar = ({ current, suffix }: { current: string; suffix: string }) => (
  <div
    className="flex flex-wrap items-center gap-2"
    style={{
      marginBottom: 14,
      paddingBottom: 12,
      borderBottom: '1px solid var(--ds-border)',
    }}
  >
    <Link
      to="/design"
      className="ds-btn"
      data-ds-variant="ghost"
      data-ds-size="sm"
    >
      <SvgIcon name="keyboardArrowLeft" size={13} />
      Directions
    </Link>

    <div className="ds-segmented" role="group" aria-label="Design direction">
      {THEMES.map((t) => (
        <Link
          key={t.id}
          to={`/design/${t.id}${suffix}`}
          className="ds-segment"
          aria-current={t.id === current ? 'page' : undefined}
          aria-selected={t.id === current}
        >
          {t.name}
        </Link>
      ))}
    </div>

    <Link
      to={`/design/${current}${suffix ? '' : '/gallery'}`}
      className="ds-btn"
      data-ds-variant="ghost"
      data-ds-size="sm"
    >
      {suffix ? 'Back to demo' : 'Component gallery'}
    </Link>

    <Link
      to="/design/compare"
      className="ds-btn"
      data-ds-variant="ghost"
      data-ds-size="sm"
    >
      Compare all
    </Link>
  </div>
);

type ThemeDemoPageProps = {
  /** Render the component gallery instead of the Warranty demo screen. */
  gallery?: boolean;
};

/**
 * One demo page per design direction.
 *
 * The five routes (`/design/precision`, `/design/saga-gold`, …) all render
 * this same component with the same content — only the active token set
 * differs. That is deliberate: it is the only way the comparison is honest.
 * Per-theme *structural* differences (Liquid Mercury's sheen, Tactile's press
 * physics) come from theme CSS hooking `data-ds-*` attributes, not from
 * branching in here.
 */
export const ThemeDemoPage = ({ gallery = false }: ThemeDemoPageProps) => {
  const { themeId } = useParams<{ themeId: string }>();
  const { theme, setTheme } = useDsTheme();

  const suffix = gallery ? '/gallery' : '';

  /**
   * URL → theme, and only that direction. The reverse (theme → URL) is handled
   * by `ThemeProvider`'s `onThemeChange` notification in DesignRoutes, because
   * an effect watching `theme` would race with this one on mount.
   *
   * The `adopted` ref makes this idempotent under `StrictMode`, which
   * double-invokes effects in development.
   */
  const adopted = useRef<string | null>(null);

  useEffect(() => {
    if (!themeId || !isThemeId(themeId)) return;
    if (adopted.current === themeId) return;
    adopted.current = themeId;
    setTheme(themeId);
  }, [themeId, setTheme]);

  if (themeId && !isThemeId(themeId)) {
    return (
      <DsRoot theme="precision" mode="light">
        <div style={{ padding: 40, background: 'var(--ds-bg)', minHeight: '100vh' }}>
          <h1 className="ds-title">Unknown design direction “{themeId}”</h1>
          <Link
            to="/design"
            className="ds-btn"
            data-ds-variant="primary"
            style={{ marginTop: 12 }}
          >
            Back to directions
          </Link>
        </div>
      </DsRoot>
    );
  }

  const meta = getTheme(theme);

  return (
    <DsRoot>
      <AppFrame context={gallery ? 'Components' : 'Warranty'}>
        <ThemeSwitcherBar current={theme} suffix={suffix} />
        {gallery ? <ComponentGallery /> : <WarrantyDemo />}

        <footer
          className="ds-muted"
          style={{
            marginTop: 24,
            paddingTop: 12,
            borderTop: '1px solid var(--ds-border)',
            fontSize: 'var(--ds-fs-xs)',
          }}
        >
          {meta?.name} · {meta?.mood} — prototype with dummy data. Use the gear
          in the header to switch direction, light/dark and density.
        </footer>
      </AppFrame>
    </DsRoot>
  );
};
